import { z } from 'zod';
import { paginationQuerySchema } from '../common/envelope.js';
/** The unattended-personal-Gmail fallback (docs section "Inbox Pulse" § Phase
 * 1) — requires 2-Step Verification + an App Password on the account, not
 * the account's real sign-in password. */
export const connectImapInputSchema = z.object({
    emailAddress: z.string().trim().toLowerCase().email(),
    imapHost: z.string().trim().min(1).max(255),
    imapPort: z.coerce.number().int().positive().default(993),
    imapUsername: z.string().trim().min(1).max(255),
    imapAppPassword: z.string().trim().min(1).max(500),
});
export const pulseConnectionSettingsSchema = z.object({
    backfillDays: z.coerce.number().int().min(1).max(365).optional(),
    excludedDomains: z.array(z.string().trim().toLowerCase().min(1)).optional(),
    outreachLabelOnly: z.boolean().optional(),
    subjectKeywordFilter: z.string().trim().max(200).nullable().optional(),
    applyGmailLabel: z.boolean().optional(),
});
export const pulseActivityLogEntrySchema = z.object({
    id: z.string().uuid(),
    direction: z.enum(['sent', 'inbound']),
    outcome: z.enum([
        'outreach_recorded',
        'lead_created',
        'activity_logged',
        'skipped_not_outreach',
        'skipped_excluded',
        'skipped_auto_reply',
        'skipped_ooo',
        'skipped_bounce',
        // AI lead qualification (Phase 1B) — a new sender's reply that AI
        // qualification decided isn't a confident-enough sales lead to create
        // automatically ('skipped_ai_review' sits in the AI Lead Review queue;
        // 'skipped_ai_rejected' was classified as not a lead at all). A
        // provider failure reuses the existing generic 'error' below rather
        // than adding a third value here.
        'skipped_ai_review',
        'skipped_ai_rejected',
        'error',
    ]),
    skipReason: z.string().nullable(),
    leadId: z.string().uuid().nullable(),
    contactId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
});
export const pulseActivityLogResponseSchema = z.object({
    stats: z.object({ processed: z.number().int(), leadsCreated: z.number().int(), errors: z.number().int() }),
    recent: z.array(pulseActivityLogEntrySchema),
});
export const pulseConnectionResponseSchema = z.object({
    id: z.string().uuid(),
    provider: z.enum(['google', 'microsoft', 'imap']),
    authMode: z.enum(['oauth', 'imap_app_password']),
    status: z.enum(['active', 'needs_reconnect', 'disabled']),
    emailAddress: z.string(),
    ownerMembershipId: z.string().uuid(),
    ownerDisplayName: z.string(),
    backfillDays: z.number().int(),
    applyGmailLabel: z.boolean(),
    outreachLabelOnly: z.boolean(),
    excludedDomains: z.array(z.string()),
    subjectKeywordFilter: z.string().nullable(),
    lastSyncAt: z.string().datetime().nullable(),
    lastSyncError: z.string().nullable(),
    createdAt: z.string().datetime(),
    /** Only populated by the tenant-wide "All connections" admin view — the
     * personal "My mailbox" list (`listMine`) leaves this unset rather than
     * compute it for what's normally 1-3 rows. */
    stats: z.object({ processed: z.number().int(), leadsCreated: z.number().int() }).optional(),
});
/** The admin-wide "All connections" view (`admin.integrations.manage`) —
 * every mailbox connection in the tenant, not just the signed-in user's
 * own. Lets whoever manages Integrations see and triage connections across
 * the whole team (e.g. "3 people need to reconnect") without being able to
 * complete someone else's OAuth consent on their behalf. */
export const listAllPulseConnectionsQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['active', 'needs_reconnect', 'disabled']).optional(),
    provider: z.enum(['google', 'microsoft', 'imap']).optional(),
});
export const pulseConnectionCountsResponseSchema = z.object({
    total: z.number().int(),
    active: z.number().int(),
    needsReconnect: z.number().int(),
    disabled: z.number().int(),
});
//# sourceMappingURL=connection.js.map